/**
 * SEO content blocks rendered below the wheel exercise.
 * Typed (not markdown) so changes are simple and visible.
 *
 * Aliases: wheel of life seo, wheel of life faq, wheel methodology, life areas
 */

import { BRAND_ORGANIZATION_ID, BRAND_URL } from "~/lib/shared/brand";

const WHEEL_CANONICAL = `${BRAND_URL}/wheel-of-life`;

// Visible copy that the HowTo JSON-LD reuses verbatim, so the schema never
// describes a step the page does not show.
const SCORE_THE_AREAS =
  "The eight standard areas are Career, Money, Health, Family, Romance, Friends, Personal Growth, and Fun. You score each from 1 to 10 based on how satisfied you feel today, and the shape across all eight reveals patterns you'd miss looking at any single area on its own.";
const READ_THE_SHAPE =
  "Your scores matter less than the shape they make. A wheel that reads 4, 4, 4, 4, 4, 4, 4, 4 is telling you something completely different from one that reads 9, 8, 1, 2, 8, 1, 2, 1, even though both average the same. Read the outline first and the individual numbers second. The section below walks through the four shapes that come up most.";
const ACT_ON_RESULTS =
  "Pick one stretched area, name what 'a one-point improvement' looks like in concrete terms, and identify the next 7-day action. The reflection and suggestions on your results page are written to help you start that conversation with yourself. When you're ready to take it further, Dream Coach Match points you to the right next step for where you are — that might be a coach, a group program, or our Life By Design tool.";

export const WHEEL_METHODOLOGY_SECTIONS = [
  {
    title: "What it measures",
    body: "The Wheel of Life captures how satisfied you currently feel across eight life areas — Career, Money, Health, Family, Romance, Friends, Personal Growth, and Fun. It isn't a diagnostic, it's a snapshot. The shape of your wheel — round, flat, spiky — surfaces patterns you might not see in a list.",
  },
  {
    title: "When it helps",
    body: "Useful at life transitions, recurring overwhelm, or after a coaching season when you want a quick read on what's shifted. Coaches use it as the first 15 minutes of an engagement; you can use it for yourself in under five.",
  },
  {
    title: "How to read your shape",
    body: READ_THE_SHAPE,
  },
] as const;

/**
 * Per-area explainers rendered below the FAQ. Order matches DEFAULT_WHEEL_LABELS
 * so the section can be color-tied to the wheel via WHEEL_WEDGE_COLORS/INK by index.
 *
 * Written to be genuinely useful to someone interpreting a low score — not
 * hidden crawler filler — while capturing secondary cluster keywords
 * (wheel of life categories, career, money, romance, etc.).
 */
export const WHEEL_AREA_EXPLAINERS = [
  {
    area: "Career",
    body: "Measures satisfaction with your current work — not just title or income, but whether the work feels meaningful, uses your strengths, and gives you room to grow. A low score rarely means 'I need a new job.' More often it signals misalignment between what you're doing and what you're capable of, or a gap between where you are and where you imagined you'd be by now. A coaching conversation here starts with one question: what would make this a 7? The answer almost always reveals the real constraint — autonomy, purpose, the relationships around you at work, or a direction you haven't yet allowed yourself to consider.",
  },
  {
    area: "Money",
    body: "Measures your emotional relationship with money — not your net worth. Someone earning $300K can score this a 2 if they feel anxious, out of control, or trapped by their income. A low score signals one of three things: actual financial pressure, a gap between income and lifestyle expectations, or a deeper story about money running quietly in the background. Coaching here isn't financial advising. It's about identifying the decisions you're avoiding, the beliefs driving those decisions, and what financial ease would actually look like — not as a number but as a feeling and a set of daily choices.",
  },
  {
    area: "Health",
    body: "Measures how you feel in your body — energy levels, sleep quality, physical strength, how often you show up as your full self. A low score is almost always a symptom of something else: a schedule that's become unsustainable, stress that's shifted into the physical, or a period of neglect you haven't had time to reverse. Coaching conversations here focus on the system, not willpower. What's actually in the way? What's the smallest change that creates the most momentum? Often the real answer isn't a fitness plan — it's a decision about what you're willing to stop doing.",
  },
  {
    area: "Family",
    body: "Measures the quality of your closest family relationships — not whether they're perfect, but whether they feel connected, present, and mutual. A low score can mean distance you've allowed to grow, unresolved tension you're managing around, or relationships that have changed and haven't been renegotiated. Coaching doesn't replace family therapy. What it does is help you get clear on what you want these relationships to actually look like, what role you're playing that you'd like to change, and what an honest conversation might open up.",
  },
  {
    area: "Romance",
    body: "Measures satisfaction with your intimate partnership — or your relationship with that absence if you're single. A low score inside an existing relationship usually signals something that needs to be said, or something that needs to be decided. For someone who's single, it often surfaces a quieter question: are you actually available — emotionally, practically, in how you're spending your time? Coaching here is about clarity before action. What do you want? What's the story you're telling yourself that's getting in the way of having it?",
  },
  {
    area: "Friends",
    body: "Measures the quality and presence of social connection outside your family. This is often the last category people notice declining and the first that's quietly eroding everything else. Common patterns: busyness that became permanent, a move or life transition that broke an old social architecture, or friendships that no longer fit who you've become. A coaching conversation here explores what meaningful connection looks like for you now — and what small, specific actions would actually rebuild it rather than just add to the calendar.",
  },
  {
    area: "Personal Growth",
    body: "Measures how much you're learning, expanding, and becoming. A high achiever can score this a 3 if they've been executing for years without space to think, read, or develop. A low score often appears alongside high Career and Money scores — you've optimized for output and quietly traded away the input. Coaching here asks a simple question: what would you be doing if performance weren't the frame? Growth conversations often unlock energy across every other area of the wheel.",
  },
  {
    area: "Fun",
    body: "Measures joy, play, lightness — the parts of life that don't produce anything. This is the most commonly neglected area among high achievers and often the most revealing. A persistent low Fun score isn't about needing a hobby. It's a signal that life has become entirely instrumental — everything serves a purpose, nothing is allowed to exist for its own sake. Coaching here takes the question seriously rather than treating it as optional. What did you used to love? What would you do if no one would know? The answers are usually more clarifying than a goal-setting session.",
  },
] as const;

/**
 * FAQ entries. `a` is the answer text — it is the single source of truth for
 * both the visible FAQ and the FAQPage JSON-LD, so the two never drift (Google
 * requires schema answer text to match the visible copy). An optional `cta`
 * renders as an inline link after the answer; it is visible-only and is not
 * part of the schema `text`.
 */
export type WheelFaq = {
  q: string;
  a: string;
  link?: { label: string; href: string };
  cta?: { label: string; href: string };
};

export const WHEEL_FAQS: readonly WheelFaq[] = [
  {
    q: "What is the Wheel of Life?",
    a: "A self-assessment tool used in coaching since the 1960s. You score eight life areas from 1 to 10, plot them as wedges of a circle, and look at the resulting shape.",
  },
  {
    q: "What are the 8 areas of the Wheel of Life?",
    a: SCORE_THE_AREAS,
  },
  {
    q: "Who created it?",
    a: "Motivational author Paul J. Meyer, founder of the Success Motivation Institute, popularised the modern form in the 1960s. The visual metaphor draws on Tibetan Buddhist iconography of life's interconnected domains.",
  },
  {
    q: "How often should I do it?",
    a: "Once every 1–3 months is the sweet spot. Less frequent and you miss drift; more frequent and the scores stop telling you anything new.",
  },
  {
    q: "What do the scores mean?",
    a: "They're how satisfied you feel today in that area — not how 'objectively good' it is. A wealthy person can score Money as 3 if they feel anxious about it. Score honestly; your wheel doesn't need to look round.",
  },
  {
    q: "How is this different from therapy?",
    a: "Therapy works deeply on what's hard. Coaching, including this exercise, works on what's next. The Wheel of Life points you at what to focus on; it doesn't process why those things are hard.",
  },
  {
    q: "What should I do with my results?",
    a: ACT_ON_RESULTS,
    cta: {
      label: "Find your next step →",
      href: "/intake?source=wheel-of-life-faq",
    },
  },
  {
    q: "Is the Wheel of Life the same as the Life Balance Wheel?",
    a: "Yes. Life Balance Wheel, Life Wheel, Coaching Wheel, and Wheel of Success all describe the same tool: a circle of life areas you rate for satisfaction. The variation in naming comes partly from Wheel of Life being a registered trademark of Success Motivation International, so many coaches and training programs adopted alternative names for the same underlying audit. This assessment works the same way whichever name brought you here.",
  },
  {
    q: "Is this Wheel of Life test free?",
    a: "Yes, completely free with no signup, no email, and no account. Score your eight areas, see your wheel, and read your reflection. If you would rather work on paper, the printable Wheel of Life template is also free to download.",
    link: {
      label: "printable Wheel of Life template",
      href: "/wheel-of-life/template",
    },
  },
  {
    q: "Can I change the life areas?",
    a: "Yes. The eight defaults are a starting point and the categories were always meant to be adjusted. Swap in Spirituality, Parenting, Creativity, Recovery, or anything else that describes a real domain of your life. The rule of thumb: a category earns a spoke if it could plausibly score very high or very low and you would notice the difference.",
  },
  {
    q: "What is a good Wheel of Life score?",
    a: "There is no good score, and comparing yours to anyone else's will mislead you. The scores are subjective and calibrated entirely to your own standards, so a 6 from one person and a 6 from another are measuring different things. What matters is the shape your scores make and the gap between where each area sits and where you want it. A wheel of straight 5s with no distress attached can describe a better life than a wheel of 8s that took everything to build.",
  },
  {
    q: "Can I save or print my wheel?",
    a: "Yes. When you finish scoring, you can print the result or save it as a PDF from the results view. Many people keep it and re-score three months later, because comparing two wheels shows movement that is otherwise easy to miss.",
  },
];

/**
 * JSON-LD @graph for /wheel-of-life. The Organization is referenced by the
 * shared brand @id only; its full node is emitted site-wide from __root.tsx.
 */
export function buildWheelJsonLd(): Record<string, unknown> {
  const steps = [
    { name: "Score the eight life areas", text: SCORE_THE_AREAS },
    { name: "Read the shape of your wheel", text: READ_THE_SHAPE },
    { name: "Act on one stretched area", text: ACT_ON_RESULTS },
  ];

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebApplication",
        "@id": `${WHEEL_CANONICAL}#app`,
        name: "Wheel of Life Assessment",
        url: WHEEL_CANONICAL,
        applicationCategory: "LifestyleApplication",
        operatingSystem: "Any",
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        publisher: { "@id": BRAND_ORGANIZATION_ID },
      },
      {
        "@type": "HowTo",
        name: "How to complete the Wheel of Life assessment",
        totalTime: "PT2M",
        step: steps.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          ...s,
        })),
      },
      {
        "@type": "FAQPage",
        mainEntity: WHEEL_FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: BRAND_URL },
          {
            "@type": "ListItem",
            position: 2,
            name: "Wheel of Life Assessment",
            item: WHEEL_CANONICAL,
          },
        ],
      },
    ],
  };
}
